import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import LinearGauge from '@amzn/meridian/linear-gauge';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tile from '@amzn/meridian/tile';
import { MetricName } from '../dashboard/constants';
import { getStatusTagType } from '../dashboard/utils';
import { useFlowColors } from '../useFlowColors';
import { ProcessPathCard } from '../flowMap/model';
import {
    BUFFER_STATE_ICONS,
    BUFFER_STATE_LABELS,
    bufferFillPercent,
    formatFlowNumber,
    getStatusLabel,
} from '../flowMap/utils';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendWindow } from './constants';
import { BufferTrendPoint } from './model';
import {
    SPARKLINE_COLORS,
    SPARKLINE_TIME_LABELS,
    actualOf,
    bufferCardLabel,
    bufferMetricOf,
    dwellTextColor,
    formatCapacityPercent,
    formatDwellDays,
    formatTrendTick,
} from './utils';

export interface BufferCardProps {
    card: ProcessPathCard;
    /** Payload-unique key; also the testid suffix. */
    cardKey: string;
    selected: boolean;
    onSelect: () => void;
    /** Id of the drill-down's heading, which a selected card points at. */
    detailsHeadingId: string;
    /** The card's trend over `trendWindow`, for its sparkline; no sparkline when absent. */
    trend?: BufferTrendPoint[];
    trendWindow: TrendWindow;
}

/** The status dot's Meridian Text colour for each status tag type. */
const STATUS_DOT_COLORS: Record<string, 'error' | 'warning' | 'success' | 'secondary'> = {
    error: 'error',
    warning: 'warning',
    success: 'success',
    neutral: 'secondary',
};

/** Sparkline height, in CSS pixels. */
const SPARKLINE_HEIGHT = 64;

/** Gauge height, in CSS pixels. */
const GAUGE_HEIGHT = 8;

/**
 * One buffer on the Buffers page: the path and its trend, the status, the units against capacity
 * as a gauge, the pallet / tote / unit breakdown, potentially lost units, and the average dwell.
 *
 * The gauge and the status dot take the BUFFER metric's server-computed status; the dot's
 * accessible name spells it out. Pressing the tile opens the drill-down below the grid; pressing it
 * again closes it. The selected tile takes the theme accent and points at the drill-down through
 * `aria-describedby`.
 */
const BufferCard: React.FC<BufferCardProps> = ({
    card,
    cardKey,
    selected,
    onSelect,
    detailsHeadingId,
    trend,
    trendWindow,
}) => {
    const buffer = bufferMetricOf(card);
    const status = buffer?.comparisonStatus ?? card.nodeHealthStatus;
    const state = buffer?.bufferState;
    const fill = bufferFillPercent(buffer?.variancePercentage);
    const lost = actualOf(card, MetricName.POTENTIAL_LOST_UNITS);
    const label = bufferCardLabel(card);
    const dwellHours = actualOf(card, MetricName.AVG_DWELL_HOURS);
    const points = (trend ?? []).map((point) => ({ time: point.timestampMs, units: point.bufferUnits }));
    const testId = `buffer-card-${cardKey}`;
    const colors = useFlowColors();
    const tagType = getStatusTagType(status);

    return (
        <Tile
            onClick={onSelect}
            accent={selected ? 'theme' : undefined}
            accentPosition="start"
            aria-describedby={selected ? detailsHeadingId : undefined}
            data-testid={testId}
            data-selected={selected ? 'true' : 'false'}
            data-status={status}
        >
            <Column spacing="200">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Row spacing="100" alignmentVertical="center">
                        <Text type="h200">{label}</Text>
                        {state && BUFFER_STATE_ICONS[state] && (
                            <Icon
                                tokens={BUFFER_STATE_ICONS[state]}
                                aria-label={BUFFER_STATE_LABELS[state] ?? state}
                                data-testid={`${testId}-state`}
                            />
                        )}
                    </Row>
                    <Text
                        type="h200"
                        color={STATUS_DOT_COLORS[tagType]}
                        role="img"
                        aria-label={getStatusLabel(status)}
                        data-testid={`${testId}-status`}
                    >
                        ●
                    </Text>
                </Row>

                <Row alignmentHorizontal="justify">
                    <Text type="b200" color="secondary" data-testid={`${testId}-units`}>
                        {formatFlowNumber(buffer?.actualValue)} / {formatFlowNumber(buffer?.expectedValue)} units
                    </Text>
                    <Text type="b200" color="secondary" data-testid={`${testId}-capacity`}>
                        {formatCapacityPercent(buffer?.variancePercentage)}
                    </Text>
                </Row>
                <LinearGauge
                    value={fill}
                    minValue={0}
                    maxValue={100}
                    fillColor={colors.statusAccent(status)}
                    height={GAUGE_HEIGHT}
                    aria-label={`${label} buffer ${Math.round(fill)}% of capacity`}
                    data-testid={`${testId}-fill`}
                />

                <Row spacing="100" wrap="down">
                    <Text type="b200" data-testid={`${testId}-breakdown`}>
                        <span aria-hidden="true">🛒 </span>
                        {formatFlowNumber(card.pallets)} pallets | <span aria-hidden="true">📦 </span>
                        {formatFlowNumber(card.totes)} totes | <span aria-hidden="true">🗒 </span>
                        {formatFlowNumber(buffer?.actualValue)} units
                    </Text>
                    {lost != null && lost > 0 && (
                        <Text type="b200" color="warning" data-testid={`${testId}-lost`}>
                            | ⚠ {formatFlowNumber(lost)} potential lost
                        </Text>
                    )}
                </Row>
                {points.length > 1 && (
                    <Box height={SPARKLINE_HEIGHT} data-testid={`${testId}-sparkline`}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={points} margin={{ top: 4, right: 4, bottom: 12, left: 0 }}>
                                <XAxis
                                    dataKey="time"
                                    type="number"
                                    scale="time"
                                    domain={['dataMin', 'dataMax']}
                                    tick={false}
                                    axisLine={false}
                                    label={{ value: SPARKLINE_TIME_LABELS[trendWindow], position: 'insideBottom', fontSize: 9 }}
                                />
                                <YAxis
                                    width={14}
                                    tick={false}
                                    axisLine={false}
                                    domain={['auto', 'auto']}
                                    label={{ value: 'Units in Buffer', angle: -90, fontSize: 9 }}
                                />
                                <Tooltip
                                    labelFormatter={(value: number) => formatTrendTick(value, trendWindow)}
                                    formatter={(value: number) => [formatFlowNumber(value), 'Units']}
                                />
                                <Line
                                    type="linear"
                                    dataKey="units"
                                    stroke={SPARKLINE_COLORS[state ?? ''] ?? SPARKLINE_COLORS.STABLE}
                                    strokeWidth={1.5}
                                    dot={{ r: 2 }}
                                    isAnimationActive={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                )}
                <Text type="h100" color={dwellTextColor(dwellHours)} data-testid={`${testId}-dwell`}>
                    Avg dwell: {formatDwellDays(dwellHours)}
                </Text>
            </Column>
        </Tile>
    );
};

export default BufferCard;
